import React, { createContext, useCallback, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '@/config/firebase';
import i18n from '@/i18n/config';
import { DEFAULT_LANGUAGE, LANGUAGES, isRtlLanguage } from '@/i18n/languages';
import { useAuthContext } from './AuthContext';
import { takeLanguageFromUrl } from '@/utils/urlLanguage';

const STORAGE_KEY = 'emi.language';

/**
 * G7 — pre-auth language detection: browser locale (`navigator.languages`,
 * falling back to `navigator.language`), matched against the language
 * codes this app knows about, itself falling back to English. This runs
 * for EVERY learner-facing page load before a profile can possibly be
 * loaded (there may be no signed-in user yet) — the profile-driven effect
 * below overrides it the moment a real preference is known.
 *
 * The one explicit exception in the spec — "the admin login page must
 * stay English regardless of browser locale" — does not correspond to a
 * real, separate route in this codebase: there is no distinct admin sign-
 * in page. `/sign-in` (SignInPage.tsx) is the SAME shared page learners
 * use; App.tsx's <AdminRoute> only checks admin-ness AFTER a successful
 * sign-in, by attempting to load /admin. Forcing English specifically on
 * /sign-in would be wrong for the overwhelming majority of visitors
 * (learners), who are exactly who G7 is for. The guarantee that actually
 * matters — admins never see a non-English DASHBOARD — is enforced two
 * other, independent ways instead: LearnerLayout.tsx never wraps any
 * /admin route (so `dir`/`lang` can never reach it), and i18n/config.ts's
 * lazyLanguageBackend never loads translated content for the `admin`
 * namespace for any language (so admin text is always English regardless
 * of whatever the instance's active language happens to be). Documented
 * here rather than silently building a route that doesn't exist.
 */
function detectInitialLanguage(): string {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && LANGUAGES.some((l) => l.code === stored)) return stored;
  } catch {
    /* private browsing / storage disabled — fall through to detection */
  }
  const known = new Set(LANGUAGES.map((l) => l.code));
  const browserLangs = (navigator.languages && navigator.languages.length
    ? navigator.languages
    : [navigator.language]
  ).filter(Boolean);
  for (const raw of browserLangs) {
    const code = raw.split('-')[0]?.toLowerCase();
    if (code && known.has(code)) return code;
  }
  return DEFAULT_LANGUAGE;
}

interface LanguageContextValue {
  language: string;
  dir: 'ltr' | 'rtl';
  /** Bumped on every completed applyLanguage() call, including a no-op
   *  one (see LanguageProvider's own comment on why this exists). Use
   *  together with `language` as a remount key — never `language` alone. */
  generation: number;
  setLanguage: (code: string) => Promise<void>;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

/**
 * G6/G7 — mounted once, high in the learner-facing tree (App.tsx), NOT
 * inside AdminLayout's subtree. Providing this context doesn't by itself
 * translate anything under /admin (admin components never read from it,
 * and even if one did, i18n/config.ts's admin-namespace exclusion still
 * holds) — the real RTL/dir boundary is LearnerLayout.tsx, a separate,
 * narrower wrapper that only surrounds the learner route subtree.
 */
export function LanguageProvider({ children }: { children: ReactNode }) {
  const { user, profile } = useAuthContext();
  // Round 142 - ?lang= from a landing-page link, taken once (utils/urlLanguage.ts); it wins over the stored / browser language and, for a
  // signed-in learner, is saved to the profile like a toggle press (below)
  const urlLanguageRef = useRef<string | null | undefined>(undefined);
  if (urlLanguageRef.current === undefined) urlLanguageRef.current = takeLanguageFromUrl(LANGUAGES.map((l) => l.code));
  const [language, setLanguageState] = useState<string>(() => urlLanguageRef.current ?? detectInitialLanguage());
  // G11 (found live, real bug) — on a fresh page load where the STORED
  // language is already non-English, `language` state starts at (say)
  // 'ar' immediately, but i18next's own internal active language always
  // boots at DEFAULT_LANGUAGE ('en') until the boot effect below calls
  // changeLanguage(). The very first render paints with plain t() calls
  // resolving through i18next's still-'en' state (real English text, not
  // a placeholder), while LearnerLayout's remount `key` was already 'ar'
  // from that same first render — so when the effect's `setLanguageState
  // ('ar')` later fires with a value REACT ALREADY HAD, React bails out
  // of the update entirely (no re-render, no remount), and the stale
  // English first paint never gets corrected. `generation` is bumped
  // UNCONDITIONALLY on every completed applyLanguage() call specifically
  // so LearnerLayout's key changes even when the language string didn't,
  // forcing the needed remount in exactly this case.
  const [generation, setGeneration] = useState(0);

  // H — out-of-order-resolution guard. `applyLanguage` is async (it awaits
  // i18next's `changeLanguage`, which for any non-English code involves a
  // real dynamic import() of that language's catalogue chunk — English is
  // eager/synchronous, every other language is not). THREE independent call
  // sites can invoke `applyLanguage`: the boot effect (fires automatically,
  // unconditionally, on every mount), the G6 profile-sync effect (also
  // automatic), and `setLanguage` (the user's own toggle/picker). Before
  // this guard, whichever call's PROMISE SETTLED LAST won — not whichever
  // was INVOKED last — because each call unconditionally wrote
  // `setLanguageState`/`setGeneration` the moment its own await resolved.
  // Concretely: a learner lands on a lesson with a non-English language
  // already stored (localStorage/profile), so the boot effect kicks off
  // `applyLanguage('ar')`, which is genuinely slow (a real network fetch of
  // the language chunk — no longer a trivial few KB now that the catalogue
  // is fully populated). If the learner switches to English (fast/eager,
  // resolves almost immediately) WHILE that boot call is still in flight,
  // the switch visibly takes effect immediately — then, seconds later, the
  // stale boot call finally resolves and silently overwrites it back to
  // Arabic, with no user action in between. Reproduced live and confirmed
  // via a controlled race (see this round's fix commit for the repro
  // recipe): switch away from the boot language before its own catalogue
  // chunk has loaded, and it reverts on its own once that chunk arrives.
  // This is almost certainly what the owner meant by "switched to English
  // ... stayed Arabic" and "needs a second click" (the second click lands
  // after the stale call has already resolved and has nothing left to
  // race against, so it sticks).
  //
  // Fix: a monotonically-increasing request token. Each call captures the
  // token value that was current when IT started; after awaiting, it only
  // commits state if no NEWER call has started in the meantime. This makes
  // "last invoked" win regardless of resolution order, which is the
  // correct semantic for "the most recent language selection wins."
  const latestRequestRef = useRef(0);

  const applyLanguage = useCallback(async (code: string): Promise<boolean> => {
    const requestId = ++latestRequestRef.current;
    await i18n.changeLanguage(code);
    if (latestRequestRef.current !== requestId) {
      // A newer applyLanguage() call started while we were awaiting —
      // this one is stale, discard it rather than clobber the newer
      // selection.
      return false;
    }
    setLanguageState(code);
    setGeneration((g) => g + 1);
    try {
      localStorage.setItem(STORAGE_KEY, code);
    } catch {
      /* ignore */
    }
    return true;
  }, []);

  // Boot: apply whatever detectInitialLanguage() resolved to.
  useEffect(() => {
    void applyLanguage(language);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // G6 — once a signed-in learner's profile has loaded, their stored
  // `preferredLanguage` (learnerProfiles/{uid}) is the source of truth
  // and overrides whatever localStorage/browser-locale guess was applied
  // before that (e.g. a different learner signed in on a shared device).
  useEffect(() => {
    const fromLink = urlLanguageRef.current;
    if (fromLink && user && profile) {
      // the language the learner chose on the landing page is their choice now: save it, as the toggle would
      urlLanguageRef.current = null;
      if (profile.preferredLanguage !== fromLink) {
        setDoc(doc(db, 'learnerProfiles', user.uid), { preferredLanguage: fromLink }, { merge: true })
          .catch((e) => console.warn('[LanguageContext] failed to persist preferredLanguage', e));
      }
      return;
    }
    if (profile?.preferredLanguage && profile.preferredLanguage !== language) {
      void applyLanguage(profile.preferredLanguage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.preferredLanguage, !!profile]);

  const setLanguage = useCallback(
    async (code: string) => {
      const committed = await applyLanguage(code);
      if (!committed) {
        // Superseded by an even newer selection before this one finished
        // loading — don't persist a choice the learner has already moved
        // on from.
        return;
      }
      if (user) {
        try {
          await setDoc(doc(db, 'learnerProfiles', user.uid), { preferredLanguage: code }, { merge: true });
        } catch (e) {
          console.warn('[LanguageContext] failed to persist preferredLanguage', e);
        }
      }
    },
    [user, applyLanguage],
  );

  return (
    <LanguageContext.Provider value={{ language, dir: isRtlLanguage(language) ? 'rtl' : 'ltr', generation, setLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage(): LanguageContextValue {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider');
  return ctx;
}

/**
 * Round 66, side note — for components that must render on BOTH learner
 * and admin screens (TopBar) but should only ever show learner-facing
 * language UI. Returns null instead of throwing when there's no
 * LanguageProvider in the tree, which is exactly the admin case:
 * LanguageProvider only ever mounts inside LearnerLayout.tsx, never above
 * /admin. Same self-hiding shape as LearnerLayout's own
 * ProblemReporterGate (useAuthContext there, useLanguageOptional here) —
 * the absence of the feature on admin is structural (no provider to read),
 * not a flag a future edit could accidentally flip on.
 */
export function useLanguageOptional(): LanguageContextValue | null {
  return useContext(LanguageContext);
}
